import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';

import lbpamDark from '../assets/landing/lbpam-dark.png';
import lbpamLight from '../assets/landing/lbpam-light.png';
import tropheesWhite from '../assets/landing/trophees-white.png';
import tropheesBlack from '../assets/landing/trophees-black.png';
import robotVisual from '../assets/landing/robot.png';
import paradoxeLogo from '../assets/landing/paradoxe.jpg';
import investanceLogo from '../assets/landing/investance-long.png';
import partenairesBanner from '../assets/landing/partenaires.jpg';

// Brand colors from the mockup
const NAVY = '#1B1E38';
const CYAN = '#39A8E5';
const BLUE = '#4A71E5';
const LIGHT = '#D5DFF0';

export default function HomePage() {
  const { user, logout } = useAuth();

  // Retour sur la landing page = déconnexion automatique
  useEffect(() => {
    if (user) logout();
  }, [user, logout]);

  // Mark <body> so the global navbar/topbar can be hidden via CSS for the
  // landing page (anonymous public face).
  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => document.body.classList.remove('landing-page');
  }, []);

  return (
    <div className="landing-root" style={{ fontFamily: "'Lato', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* ======================== TOP MENU ======================== */}
      <header className="lp-topbar">
        <Link to="/" className="lp-topbar-brand">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <rect width="32" height="32" rx="8" fill={BLUE}/>
            <text x="6" y="23" fontSize="18" fontWeight="bold" fill="white">ISI</text>
          </svg>
          <span>Plateforme ISI <em className="lp-topbar-brand-sub">by Paradoxe.ai for LBP AM</em></span>
        </Link>
        <nav className="lp-topbar-nav">
          <Link
            to="/faq"
            className="lp-topbar-link"
            onMouseDown={(e) => e.preventDefault()}
          >FAQ</Link>
          <a href="mailto:contact.tropheeslbpam@investance-partners.com"
             className="lp-topbar-link">Nous contacter</a>
          <Link
            to="/login"
            className="lp-topbar-link"
            onMouseDown={(e) => e.preventDefault()}
          >Se connecter</Link>
          <Link
            to="/register"
            className="lp-topbar-cta"
            onMouseDown={(e) => e.preventDefault()}
          >Créer un compte</Link>
        </nav>
      </header>

      {/* ======================== HERO ======================== */}
      <section className="lp-hero">
        <div className="lp-hero-grid">
          <div className="lp-hero-text">
            <a
              href="https://www.lbpam.com/fr/notre-identite/la-tech-au-service-de-nos-metiers/trophees-de-l-innovation"
              target="_blank" rel="noopener noreferrer"
              title="En savoir plus sur Les Trophées de l'innovation LBP AM"
              className="lp-trophees-logo-link"
            >
              <img
                src={tropheesWhite}
                alt="Les Trophées de l'innovation"
                className="lp-trophees-logo"
              />
            </a>
            <div className="lp-hero-rule" />
            <p className="lp-hero-paragraph">
              Depuis 2022, LBP AM récompense les entreprises les plus innovantes avec Les
              Trophées de l'innovation.
            </p>
            <p className="lp-hero-paragraph">
              Pour cette 5<sup>ème</sup> édition, <strong>3 French Tech</strong> seront récompensées.
            </p>
          </div>
          <div className="lp-hero-visual" aria-hidden="true">
            <img src={robotVisual} alt="" className="lp-hero-robot" />
          </div>
        </div>
        <div className="lp-hero-lbpam">
          <img src={lbpamDark} alt="LBPAM" />
        </div>
      </section>

      {/* ======================== PRÉSENTATION + KPIs ======================== */}
      <section className="lp-presentation">
        <div className="lp-presentation-inner">
          <div className="lp-presentation-text">
            <p>
              Organisés en partenariat avec{' '}
              <a href="https://www.investance-partners.com" target="_blank" rel="noopener noreferrer"
                 className="lp-inline-link"><strong>Investance Partners</strong></a>,{' '}
              les trophées ont pour objectif de valoriser et d'accompagner des entreprises
              françaises innovantes, capables de devenir des acteurs clés d'une économie durable.
            </p>
            <p>
              <a href="https://www.lbpam.com/" target="_blank" rel="noopener noreferrer"
                 className="lp-inline-link" title="Visiter le site de LBP AM">
                <strong>LBP AM</strong>
              </a>{' '}
              s'appuie sur un jury prestigieux qui associe des professionnels de
              l'investissement coté et non coté, des investisseurs institutionnels, ainsi que des
              experts des domaines de la Tech.
            </p>
            <p>
              <a href="https://www.lbpam.com/" target="_blank" rel="noopener noreferrer"
                 className="lp-inline-link" title="Visiter le site de LBP AM">
                <strong>LBP AM</strong>
              </a>{' '}
              peut également compter sur le soutien de partenaires de premier plan qui
              contribuent à soutenir les French Tech et de devenir les licornes de demain.
            </p>
          </div>
          <div className="lp-presentation-logo">
            <a
              href="https://www.lbpam.com/fr/notre-identite/la-tech-au-service-de-nos-metiers/trophees-de-l-innovation"
              target="_blank" rel="noopener noreferrer"
              title="En savoir plus sur Les Trophées de l'innovation LBP AM"
            >
              <img src={tropheesBlack} alt="Les Trophées de l'innovation" />
            </a>
          </div>
        </div>

        <div className="lp-kpis">
          <KPI value="5" suffix={null} label={<>éditions<br/>depuis<br/>2022</>} />
          <KPI value="+100" label={<>startups<br/>sélectionnées<br/>sur dossier</>} />
          <KPI value="3" label={<>French Tech<br/>Récompensées</>} />
          <KPI value="10k€" label={<>de dotation financière<br/>&amp; de nombreux autres<br/>bénéfices</>} />
        </div>

        <div className="lp-cta">
          <Link to="/register" className="lp-cta-btn">
            Je candidate
            <span className="lp-cta-arrow">→</span>
          </Link>
        </div>
      </section>

      {/* ======================== CALENDRIER ======================== */}
      <section className="lp-calendar">
        <h2 className="lp-section-title">Le calendrier 2026</h2>
        <div className="lp-calendar-grid">
          <CalendarCard date="11 mai" label={<>Ouverture des<br/>candidatures</>} />
          {/* Date reportée : l'ancienne date reste visible barrée pour que les
              candidats qui l'avaient notée comprennent le changement. */}
          <CalendarCard
            date={<>
              <s className="lp-calendar-date-old">31 août</s>
              {' '}
              <span className="lp-calendar-date-new">11 septembre</span>
            </>}
            label={<>La clôture des candidatures est repoussée au 11 septembre</>}
          />
          <CalendarCard date="14 oct." label={<>Audition<br/>des finalistes</>} />
          <CalendarCard date="18 nov." label={<>Cérémonie 2026<br/>à l'Elysées Biarritz<br/>Paris 8<sup>e</sup></>} />
        </div>
      </section>

      {/* ======================== BANNIÈRE PARTENAIRES ======================== */}
      <section className="lp-partners-banner">
        <img
          src={partenairesBanner}
          alt="Partenaires des Trophées de l'innovation LBP AM 2026 : Investance Partners, Deloitte, Bpifrance, Le Hub Bpifrance, OVHcloud, B Smart, LightOn, Finance Innovation, Image Sept"
          className="lp-partners-banner-img"
        />
      </section>

      {/* ======================== ORGANISATEURS / MARQUE ======================== */}
      <section className="lp-partners">
        <div className="lp-partners-inner">
          {/* Co-organisateurs et marque — alignés horizontalement */}
          <div className="lp-organizers-row">
            <img src={paradoxeLogo} alt="Paradoxe AI" className="lp-organizer-logo" />
            <a href="https://investance-partners.com/" target="_blank" rel="noopener noreferrer"
               className="lp-organizer-link" title="Visiter le site d'Investance Partners">
              <img src={investanceLogo} alt="Investance Partners" className="lp-organizer-logo lp-organizer-investance" />
            </a>
            <a
              href="https://www.lbpam.com/fr/notre-identite/la-tech-au-service-de-nos-metiers/trophees-de-l-innovation"
              target="_blank" rel="noopener noreferrer"
              className="lp-organizer-link"
              title="En savoir plus sur Les Trophées de l'innovation LBP AM"
            >
              <img src={tropheesWhite} alt="Les Trophées de l'innovation" className="lp-organizer-logo lp-organizer-trophees" />
            </a>
          </div>
        </div>
      </section>

      {/* Discreet auth links for users coming back */}
      <footer className="lp-footer">
        <Link to="/login">Espace candidat / jury</Link>
        <span className="lp-footer-sep" aria-hidden="true">·</span>
        <Link to="/mentions-legales">Mentions légales</Link>
        <span className="lp-footer-sep" aria-hidden="true">·</span>
        <Link to="/politique-confidentialite">Politique de confidentialité</Link>
        <span className="lp-footer-sep" aria-hidden="true">·</span>
        <Link to="/cgu">CGU</Link>
        <span className="lp-footer-sep" aria-hidden="true">·</span>
        <Link to="/reglement">Règlement des trophées de l'innovation 2026</Link>
      </footer>

      {/* ======================== INLINE STYLES ======================== */}
      <style>{`
        body.landing-page .navbar,
        body.landing-page .topbar { display: none !important; }
        body.landing-page .main-content {
          margin-left: 0 !important;
          padding-top: 0 !important;
        }
        body.landing-page { background: ${NAVY}; }

        .landing-root {
          color: ${NAVY};
          line-height: 1.55;
        }
        .landing-root strong { font-weight: 700; }
        .lp-inline-link {
          color: ${BLUE};
          text-decoration: none;
          border-bottom: 1px solid ${BLUE};
          transition: color 0.15s, border-color 0.15s;
        }
        .lp-inline-link:hover { color: ${CYAN}; border-bottom-color: ${CYAN}; }
        .lp-inline-link strong { color: inherit; }

        /* ---------- TOP BAR ---------- */
        .lp-topbar {
          position: sticky;
          top: 0;
          z-index: 50;
          height: 64px;
          padding: 0 6vw;
          background: ${NAVY};
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .lp-topbar-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          font-size: 17px;
          letter-spacing: 0.4px;
          text-decoration: none;
          white-space: nowrap;
        }
        .lp-topbar-brand svg { flex-shrink: 0; }
        .lp-topbar-brand:hover { color: ${CYAN}; }
        .lp-topbar-brand-sub {
          font-style: normal;
          font-weight: 400;
          font-size: 13px;
          color: ${CYAN};
          margin-left: 6px;
          letter-spacing: 0.2px;
        }
        .lp-topbar-nav { display: flex; align-items: center; gap: 14px; }
        .lp-topbar-link,
        .lp-topbar-cta {
          font-family: 'Lato', sans-serif;
          font-size: 14px;
          font-weight: 600;
          padding: 9px 18px;
          border-radius: 999px;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
          /* Reset to avoid sticky :active/:focus paint on mouse clicks */
          outline: none;
          -webkit-tap-highlight-color: transparent;
        }
        .lp-topbar-link {
          color: rgba(255,255,255,0.85);
          background: transparent;
          border: 1px solid transparent;
        }
        .lp-topbar-link:hover { color: white; background: rgba(255,255,255,0.08); }
        .lp-topbar-cta {
          color: white !important;
          background: ${BLUE};
          border: 1px solid ${BLUE};
        }
        .lp-topbar-cta:hover { background: #3B5FCF; border-color: #3B5FCF; color: white !important; }
        /* Belt-and-braces: explicitly clear any focus ring left after a
           mouse click in browsers that briefly paint :focus before
           :focus-visible resolves. */
        .lp-topbar-link:focus:not(:focus-visible),
        .lp-topbar-cta:focus:not(:focus-visible) {
          box-shadow: none;
          outline: none;
        }
        /* Show a focus ring ONLY for keyboard navigation (not after mouse click) */
        .lp-topbar-link:focus-visible,
        .lp-topbar-cta:focus-visible {
          box-shadow: 0 0 0 3px rgba(57,168,229,0.55);
        }

        /* ---------- HERO ---------- */
        .lp-hero {
          position: relative;
          background: ${NAVY};
          color: white;
          padding: 8px 6vw 220px;
          overflow: hidden;
          min-height: 80vh;
        }
        .lp-hero-grid {
          display: grid;
          grid-template-columns: 1.05fr 1fr;
          gap: 32px;
          align-items: center;
          max-width: 1320px;
          margin: 0 auto;
        }
        .lp-hero-text { max-width: 540px; }
        .lp-trophees-logo-link {
          display: inline-block;
          text-decoration: none;
          transition: transform 0.2s ease, filter 0.2s ease;
        }
        .lp-trophees-logo-link:hover { transform: scale(1.02); filter: brightness(1.05); }
        .lp-trophees-logo-link:focus-visible {
          outline: 2px solid ${CYAN};
          outline-offset: 4px;
          border-radius: 4px;
        }
        .lp-trophees-logo {
          width: 100%;
          max-width: 460px;
          height: auto;
          display: block;
        }
        /* Logo Trophées dans la section présentation : même feedback de hover */
        .lp-presentation-logo a {
          display: inline-block;
          text-decoration: none;
          transition: transform 0.2s ease;
        }
        .lp-presentation-logo a:hover { transform: scale(1.02); }
        .lp-presentation-logo a:focus-visible {
          outline: 2px solid ${CYAN};
          outline-offset: 4px;
          border-radius: 4px;
        }
        .lp-hero-rule {
          width: 96px;
          height: 4px;
          background: ${CYAN};
          margin: 24px 0 36px;
          border-radius: 2px;
        }
        .lp-hero-paragraph {
          font-size: 18px;
          line-height: 1.6;
          color: rgba(255,255,255,0.92);
          margin-bottom: 16px;
        }
        .lp-hero-paragraph sup { font-size: 0.65em; }

        .lp-hero-visual {
          position: relative;
          height: 540px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .lp-hero-robot {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
          /* The image already has the same navy background as the hero,
             but a slight glow softens any seam if the canvas dims differ. */
          filter: drop-shadow(0 0 60px rgba(74,113,229,0.18));
        }
        .lp-hero-lbpam {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 32px;
          display: flex;
          justify-content: center;
          z-index: 2;
        }
        .lp-hero-lbpam img { height: 168px; width: auto; display: block; }

        /* ---------- PRÉSENTATION + KPIs ---------- */
        .lp-presentation {
          background: white;
          padding: 80px 6vw 56px;
        }
        .lp-presentation-inner {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 48px;
          align-items: start;
          max-width: 1320px;
          margin: 0 auto 56px;
        }
        .lp-presentation-text p {
          font-size: 18px;
          line-height: 1.7;
          margin-bottom: 24px;
          color: ${NAVY};
        }
        .lp-presentation-text p:last-child { margin-bottom: 0; }
        .lp-presentation-logo img {
          width: 100%;
          max-width: 260px;
          height: auto;
          display: block;
          margin-left: auto;
        }
        .lp-kpis {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 1320px;
          margin: 0 auto 48px;
        }
        .lp-kpi {
          background: #EEF1F6;
          border-radius: 18px;
          padding: 32px 20px;
          text-align: center;
        }
        .lp-kpi-value {
          font-size: 56px;
          font-weight: 700;
          color: ${CYAN};
          line-height: 1;
          margin-bottom: 16px;
          font-family: 'Lato', sans-serif;
        }
        .lp-kpi-label {
          font-size: 16px;
          color: ${NAVY};
          font-weight: 400;
        }
        .lp-cta {
          display: flex;
          justify-content: center;
        }
        .lp-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 24px;
          background: ${BLUE};
          color: white !important;
          padding: 18px 56px;
          font-size: 22px;
          font-weight: 700;
          border-radius: 999px;
          text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
          box-shadow: 0 6px 24px rgba(74,113,229,0.3);
        }
        .lp-cta-btn:hover {
          background: #3B5FCF;
          transform: translateY(-1px);
          box-shadow: 0 10px 28px rgba(74,113,229,0.42);
          color: white !important;
        }
        .lp-cta-arrow { font-size: 24px; }

        /* ---------- CALENDRIER ---------- */
        .lp-calendar {
          background: ${LIGHT};
          padding: 72px 6vw;
        }
        .lp-section-title {
          font-family: 'Lato', sans-serif;
          font-size: 38px;
          font-weight: 700;
          color: ${BLUE};
          max-width: 1320px;
          margin: 0 auto 36px;
        }
        .lp-calendar-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 1320px;
          margin: 0 auto;
        }
        .lp-calendar-card {
          background: white;
          border-radius: 18px;
          padding: 32px 24px;
          text-align: left;
          /* sans min-width, la date insecable "11 septembre" elargirait
             sa colonne au detriment des trois autres cartes */
          min-width: 0;
        }
        .lp-calendar-date {
          font-size: 38px;
          font-weight: 700;
          color: ${BLUE};
          line-height: 1.12;
          margin-bottom: 24px;
          font-family: 'Lato', sans-serif;
        }
        .lp-calendar-label {
          font-size: 16px;
          color: ${NAVY};
          line-height: 1.45;
        }
        .lp-calendar-label sup { font-size: 0.7em; }
        /* Les deux dates sont volontairement reduites pour tenir cote a cote
           dans la largeur d'une carte ; sur ecran etroit elles passent a la ligne. */
        .lp-calendar-date-old {
          font-size: 0.55em;
          color: ${NAVY};
          opacity: 0.45;
          white-space: nowrap;
          text-decoration-thickness: 2px;
        }
        .lp-calendar-date-new { white-space: nowrap; font-size: 0.72em; }

        /* ---------- BANNIÈRE PARTENAIRES ---------- */
        .lp-partners-banner {
          background: ${LIGHT};
          padding: 0 6vw 72px;
        }
        .lp-partners-banner-img {
          display: block;
          width: 100%;
          max-width: 1320px;
          height: auto;
          margin: 0 auto;
          border-radius: 18px;
        }

        /* ---------- PARTENAIRES ---------- */
        .lp-partners {
          background: ${NAVY};
          padding: 64px 6vw 48px;
          position: relative;
        }
        .lp-partners-inner {
          max-width: 1320px;
          margin: 0 auto;
        }
        .lp-section-title-light { color: white; margin-bottom: 28px; }
        /* Co-organisateurs row, aligned horizontally */
        .lp-organizers-row {
          display: flex;
          align-items: center;
          justify-content: space-around;
          gap: 32px;
          flex-wrap: wrap;
        }
        .lp-organizer-logo {
          height: 70px;
          width: auto;
          display: block;
          object-fit: contain;
          border-radius: 8px;
          transition: transform 0.2s;
        }
        .lp-organizer-logo:hover { transform: scale(1.04); }
        .lp-organizer-link {
          display: inline-flex;
          text-decoration: none;
          outline: none;
          -webkit-tap-highlight-color: transparent;
          border-radius: 8px;
        }
        .lp-organizer-link:focus-visible { box-shadow: 0 0 0 3px rgba(57,168,229,0.55); }
        /* The Trophées logo is a transparent PNG on dark, give it a touch more height */
        .lp-organizer-trophees { height: 78px; border-radius: 0; }
        /* The Investance long logo is a transparent PNG with content reaching the
           top edge — disable border-radius (which clipped it) and lower it a bit
           so the upper-left corner is fully visible. */
        .lp-organizer-investance {
          border-radius: 0;
          margin-top: 12px;
        }

        /* ---------- FOOTER ---------- */
        .lp-footer {
          background: ${NAVY};
          padding: 24px 6vw 28px;
          text-align: center;
        }
        .lp-footer a {
          color: rgba(255,255,255,0.55);
          font-size: 13px;
          text-decoration: none;
        }
        .lp-footer a:hover { color: white; }
        .lp-footer-sep {
          color: rgba(255,255,255,0.30);
          font-size: 13px;
          margin: 0 12px;
          user-select: none;
        }

        /* ---------- RESPONSIVE ---------- */
        @media (max-width: 900px) {
          .lp-topbar { padding: 0 4vw; height: 56px; }
          .lp-topbar-brand { font-size: 14px; gap: 8px; }
          .lp-topbar-brand-sub { display: none; }
          .lp-topbar-link, .lp-topbar-cta { padding: 8px 14px; font-size: 13px; }
          .lp-hero { padding: 8px 5vw 180px; }
          .lp-hero-grid { grid-template-columns: 1fr; gap: 24px; }
          .lp-hero-visual { height: 280px; }
          .lp-hero-lbpam img { height: 96px; }
          .lp-presentation { padding: 56px 5vw 40px; }
          .lp-presentation-inner { grid-template-columns: 1fr; }
          .lp-presentation-logo img { margin: 0 auto; }
          .lp-kpis { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .lp-calendar { padding: 56px 5vw; }
          .lp-calendar-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .lp-section-title { font-size: 26px; margin-bottom: 24px; }
          .lp-kpi-value, .lp-calendar-date { font-size: 40px; }
          .lp-partners { padding: 48px 5vw; }
          .lp-organizers-row { gap: 24px; justify-content: center; }
          .lp-organizer-logo { height: 56px; }
          .lp-organizer-trophees { height: 64px; }
        }
        @media (max-width: 520px) {
          /* Les quatre liens font 449 px : ils débordaient de l'écran. La marque
             occupe la première ligne, les liens passent dessous et se replient. */
          .lp-topbar { height: auto; flex-wrap: wrap; gap: 8px; padding: 10px 4vw; }
          .lp-topbar-nav { width: 100%; flex-wrap: wrap; gap: 8px; }
          .lp-topbar-link, .lp-topbar-cta { padding: 8px 12px; font-size: 12.5px; }
          .lp-kpis, .lp-calendar-grid { grid-template-columns: 1fr; }
          .lp-cta-btn { padding: 14px 32px; font-size: 16px; gap: 16px; }
          .lp-hero-paragraph { font-size: 16px; }
          .lp-presentation-text p { font-size: 16px; }
          .lp-hero-lbpam img { height: 72px; }
        }
      `}</style>
    </div>
  );
}

function KPI({ value, label }) {
  return (
    <div className="lp-kpi">
      <div className="lp-kpi-value">{value}</div>
      <div className="lp-kpi-label">{label}</div>
    </div>
  );
}

function CalendarCard({ date, label }) {
  return (
    <div className="lp-calendar-card">
      <div className="lp-calendar-date">{date}</div>
      <div className="lp-calendar-label">{label}</div>
    </div>
  );
}
